<template>
    <DataTable
        :data="users"
        :columns="userColumns"
        :loading="false"
        enable-sorting
        enable-search
        :search-placeholder="searchPlaceholder"
        @row-click="openUserDetails"
    />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { h } from 'vue';
import { useRouter } from 'vue-router';
import type { ColumnDef } from '@tanstack/vue-table';
import DataTable from '@/components/tables/DataTable.vue';
import { createUserInfoColumn, createTextColumn, createCountColumn } from '@/components/tables/columns/factories';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import type { AdminUserEstablished } from '@/stores/store-interfaces';

defineProps<{
    searchInput: string;
    users: AdminUserEstablished[];
    searchPlaceholder?: string;
}>();

const router = useRouter();

// The row (or the whole card on mobile) is the tap target for user details.
function openUserDetails(row: AdminUserEstablished) {
    router.push({ name: 'user-details', params: { userId: row.id } });
}

// Column definitions
const userColumns = computed<ColumnDef<AdminUserEstablished, unknown>[]>(() => [
    createUserInfoColumn<AdminUserEstablished>(),

    // Username column
    createTextColumn<AdminUserEstablished>('username', 'Username'),

    // Email column
    createTextColumn<AdminUserEstablished>('email', 'Email'),

    // Organization count column
    createCountColumn<AdminUserEstablished>('organization_count', 'Organizations'),

    // Role column with conditional tag styling
    {
        accessorKey: 'role',
        header: 'Role',
        cell: (info) => {
            const role = info.getValue() as string;

            if (!role) {
                return '-';
            }

            return h(TagComponent, {
                value: role,
                variant: role === 'org-admin' ? 'hold' : 'default',
                className: role === 'org-user' ? 'text-(--state-info-fg)' : '',
            });
        },
        enableSorting: true,
    },
]);
</script>
